import { Stack, Typography } from '@mui/material';
import { compact } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { AI_ONBOARDING_MODAL_VARIANT_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.constants';
import { AIOnboardingModalVariant } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { getAIOnboardingModalHeader } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.utils';
import { AIOnboardingModalFooterActions } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalFooter';
import {
  AIOnboardingModalFormShape,
  AIOnboardingModalFormShapeKeys,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import {
  appendSuggestionsForSubmissionToFormData,
  formDataToInput,
  getFormDataForSuggestion,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.utils';
import { AIOnboardingModalFormTable } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalFormTable';
import { AIOnboardingModalPagination } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalPagination';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import {
  AiOnboardingModal_HouseholdFragment,
  AiOnboardingModalBulkCreateMutationVariables,
  useAiOnboardingModalBulkCreateMutation,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { useAcknowledgeAiSuggestionsMutation } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import {
  AiSuggestionAcceptanceStatus,
  HouseholdBillableKind,
} from '@/types/schema';
import { PathsOf } from '@/types/subform';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError, ValidationError } from '@/utils/errors';

interface AIOnboardingModalFormInnerProps {
  initialValues: AIOnboardingModalFormShape;
  formTableConfigs: {
    header: string;
    formPath: AIOnboardingModalFormShapeKeys;
  }[];
}

function AIOnboardingModalFormInner({
  initialValues,
  formTableConfigs,
}: AIOnboardingModalFormInnerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const navigate = useNavigate();

  const {
    householdId,
    billableKind,
    primaryClients,
    possibleGrantors,
    entities,
    isTwoClientHousehold,
    documents,
  } = useHouseholdDetailsContext();

  const {
    variant,
    footerRef,
    suggestionsByID,
    suggestionsForSubmission,
    principalNodesById,
    reviewedSuggestionIDs,
    onClose,
    onComplete,
    onSuggestionSaved,
    skipNavigationOnComplete,
    isOperationInFlight,
    beginOperation,
    endOperation,
  } = useAIOnboardingModalContext();

  const [bulkCreateMutation] = useAiOnboardingModalBulkCreateMutation();
  const [acknowledgeSuggestion] = useAcknowledgeAiSuggestionsMutation();

  const { getValues, handleSubmit, trigger } =
    useFormContext<AIOnboardingModalFormShape>();

  const pendingSuggestionIDsRef = useRef(new Set<string>());
  const startingSuggestionIDsRef = useRef(new Set<string>());
  const [pendingSuggestionIDs, setPendingSuggestionIDs] = useState<string[]>(
    []
  );

  const beginSuggestionOperation = useCallback((suggestionID: string) => {
    if (pendingSuggestionIDsRef.current.has(suggestionID)) {
      return false;
    }

    pendingSuggestionIDsRef.current.add(suggestionID);
    setPendingSuggestionIDs(Array.from(pendingSuggestionIDsRef.current));
    return true;
  }, []);

  const endSuggestionOperation = useCallback((suggestionID: string) => {
    pendingSuggestionIDsRef.current.delete(suggestionID);
    setPendingSuggestionIDs(Array.from(pendingSuggestionIDsRef.current));
  }, []);

  const household = useMemo<AiOnboardingModal_HouseholdFragment>(
    () => ({
      id: householdId ?? '',
      billableKind: billableKind || HouseholdBillableKind.Billable,
      possiblePrimaryClients: primaryClients ?? [],
      possibleGrantors: possibleGrantors ?? [],
      isTwoGrantor: isTwoClientHousehold,
      documents,
      entities: { edges: entities?.map((entity) => ({ node: entity })) || [] },
    }),
    [
      billableKind,
      documents,
      entities,
      householdId,
      isTwoClientHousehold,
      possibleGrantors,
      primaryClients,
    ]
  );

  const handleMutationError = useCallback(
    (message: string, error: Error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(message, error);
    },
    [reportError, showFeedback]
  );

  const { onCompleteNavigationRoute } =
    AI_ONBOARDING_MODAL_VARIANT_CONFIGS[variant];

  const submitBulkCreateMutation = useCallback(
    async (input: AiOnboardingModalBulkCreateMutationVariables) => {
      return await bulkCreateMutation({
        variables: input,
        onError: (error) => {
          handleMutationError(
            'error in bulkCreateMutation from AISuggestionsModal',
            error
          );
        },
        onCompleted: () => {
          diagnostics.debug(
            '[AIOnboardingModalProvider][bulkCreateMutation][onCompleted] - bulk create completed'
          );

          if (!householdId) {
            // This shouldn't happen; just a type safety check.
            return;
          }

          onComplete();

          if (!skipNavigationOnComplete) {
            navigate(
              getCompletePathFromRouteKey(onCompleteNavigationRoute, {
                householdId,
              })
            );
          }
        },
      });
    },
    [
      bulkCreateMutation,
      handleMutationError,
      householdId,
      navigate,
      onComplete,
      onCompleteNavigationRoute,
      skipNavigationOnComplete,
    ]
  );

  const onValidSubmission = useCallback(
    async (formData: AIOnboardingModalFormShape) => {
      if (!householdId) {
        // This shouldn't happen; just a type safety check.
        return;
      }

      // Get the form paths for the current variant.
      const formPaths = AI_ONBOARDING_MODAL_VARIANT_CONFIGS[variant].formPaths;

      let acceptedSuggestionIDs: string[];
      let rest: Omit<
        AiOnboardingModalBulkCreateMutationVariables,
        'acknowledgeSuggestionsInput'
      >;
      try {
        // The paginated form only holds the current page's rows, but submission
        // always covers the modal's whole scope: pending suggestions on other
        // pages are appended with their suggested values.
        const fullScopeFormData = appendSuggestionsForSubmissionToFormData(
          formData,
          suggestionsForSubmission,
          {
            formPaths,
            excludedSuggestionIDs: reviewedSuggestionIDs,
            household,
            principalNodesById,
          }
        );

        // The data in input and acceptedSuggestionIDs only contains the fields
        // that are in the given formPaths.
        ({ acceptedSuggestionIDs, ...rest } = formDataToInput(
          fullScopeFormData,
          formPaths,
          household,
          suggestionsByID
        ));
      } catch (error) {
        if (error instanceof ValidationError) {
          showFeedback(error.message);
          return;
        }

        reportError(
          'Failed to prepare suggestions for submission',
          error instanceof Error ? error : new Error(String(error)),
          {
            householdId,
            variant,
            suggestionIds: suggestionsForSubmission
              .map((suggestion) => suggestion.id)
              .join(','),
          }
        );
        showFeedback(FeedbackMessages.formSaveError);
        return;
      }

      const fullInput: AiOnboardingModalBulkCreateMutationVariables = {
        ...rest,
        acknowledgeSuggestionsInput: [
          ...acceptedSuggestionIDs.map((id) => ({
            suggestionID: id,
            status: AiSuggestionAcceptanceStatus.Accepted,
          })),
        ],
      };

      return await submitBulkCreateMutation(fullInput);
    },
    [
      household,
      householdId,
      principalNodesById,
      reviewedSuggestionIDs,
      submitBulkCreateMutation,
      suggestionsByID,
      suggestionsForSubmission,
      variant,
      reportError,
      showFeedback,
    ]
  );

  const onAcceptSuggestion = useCallback(
    async ({
      formPath,
      suggestionID,
    }: {
      formPath: AIOnboardingModalFormShapeKeys;
      suggestionID: string;
    }) => {
      if (startingSuggestionIDsRef.current.has(suggestionID)) {
        return false;
      }

      startingSuggestionIDsRef.current.add(suggestionID);
      let modalOperationStarted = false;
      let suggestionOperationStarted = false;
      try {
        const index = getValues(formPath).findIndex(
          (row) => row._suggestionID === suggestionID
        );
        if (
          index === -1 ||
          !(await trigger(`${formPath}.${index}`, { shouldFocus: true }))
        ) {
          return false;
        }

        modalOperationStarted = beginOperation();
        if (!modalOperationStarted) {
          return false;
        }

        suggestionOperationStarted = beginSuggestionOperation(suggestionID);
        if (!suggestionOperationStarted) {
          return false;
        }

        const suggestionFormData = getFormDataForSuggestion(
          getValues(),
          suggestionID
        );
        const { acceptedSuggestionIDs: _acceptedSuggestionIDs, ...input } =
          formDataToInput(
            suggestionFormData,
            [formPath],
            household,
            suggestionsByID
          );
        const result = await bulkCreateMutation({
          variables: {
            ...input,
            acknowledgeSuggestionsInput: [
              {
                suggestionID,
                status: AiSuggestionAcceptanceStatus.Accepted,
              },
            ],
          },
          onError: (error) => {
            handleMutationError(
              'Failed to accept AI onboarding suggestion',
              error
            );
          },
        });
        if (!result.data || result.errors?.length) {
          return false;
        }

        const { isFinalSuggestion } = onSuggestionSaved(suggestionID);
        trackUserEvent('ai_onboarding confirm suggested row', { formPath });
        showFeedback('Suggestion successfully applied', {
          variant: 'success',
        });
        if (isFinalSuggestion) {
          endOperation();
          modalOperationStarted = false;
          onClose();
        }
        return true;
      } catch (error) {
        if (error instanceof ValidationError) {
          showFeedback(error.message);
          return false;
        }

        handleMutationError(
          'Failed to accept AI onboarding suggestion',
          error instanceof Error ? error : new Error(String(error))
        );
        return false;
      } finally {
        startingSuggestionIDsRef.current.delete(suggestionID);
        if (suggestionOperationStarted) {
          endSuggestionOperation(suggestionID);
        }
        if (modalOperationStarted) {
          endOperation();
        }
      }
    },
    [
      beginOperation,
      beginSuggestionOperation,
      bulkCreateMutation,
      endOperation,
      endSuggestionOperation,
      getValues,
      handleMutationError,
      household,
      onClose,
      onSuggestionSaved,
      showFeedback,
      suggestionsByID,
      trackUserEvent,
      trigger,
    ]
  );

  const onRejectSuggestion = useCallback(
    async (suggestionID: string) => {
      if (!beginSuggestionOperation(suggestionID)) {
        return false;
      }

      try {
        const result = await acknowledgeSuggestion({
          variables: {
            input: [
              {
                suggestionID,
                status: AiSuggestionAcceptanceStatus.Rejected,
              },
            ],
          },
        });
        if (!result.data || result.errors?.length) {
          return false;
        }

        onSuggestionSaved(suggestionID);
        showFeedback('Suggestion rejected', { variant: 'success' });
        return true;
      } catch (error) {
        handleMutationError(
          'Failed to reject AI onboarding suggestion',
          error instanceof Error ? error : new Error(String(error))
        );
        return false;
      } finally {
        endSuggestionOperation(suggestionID);
      }
    },
    [
      acknowledgeSuggestion,
      beginSuggestionOperation,
      endSuggestionOperation,
      handleMutationError,
      onSuggestionSaved,
      showFeedback,
    ]
  );

  const onSubmit = useCallback(async () => {
    if (!beginOperation()) {
      return;
    }

    try {
      await handleSubmit((values) => onValidSubmission(values))();
    } finally {
      endOperation();
    }
  }, [beginOperation, endOperation, handleSubmit, onValidSubmission]);

  const hasSuggestionOperationInFlight = pendingSuggestionIDs.length > 0;

  return (
    <>
      {formTableConfigs.map((configs) => {
        return (
          <AIOnboardingModalFormTable
            key={configs.formPath}
            {...configs}
            onAcceptSuggestion={onAcceptSuggestion}
            onRejectSuggestion={onRejectSuggestion}
            beginOperation={beginOperation}
            endOperation={endOperation}
            disabled={isOperationInFlight}
            pendingSuggestionIDs={pendingSuggestionIDs}
          />
        );
      })}
      {footerRef.current &&
        createPortal(
          <AIOnboardingModalFooterActions
            onSubmit={onSubmit}
            disabled={isOperationInFlight}
            submitDisabled={hasSuggestionOperationInFlight}
          />,
          footerRef.current
        )}
      <AIOnboardingModalPagination
        initialValues={initialValues}
        disabled={isOperationInFlight || hasSuggestionOperationInFlight}
      />
    </>
  );
}

interface AIOnboardingModalFormProps {
  initialValues: AIOnboardingModalFormShape;
}

export function AIOnboardingModalForm({
  initialValues,
}: AIOnboardingModalFormProps) {
  const { variant, isOperationInFlight } = useAIOnboardingModalContext();

  const formMethods = useForm<AIOnboardingModalFormShape>({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const { title, subtitle, tableConfigs } = useMemo(() => {
    const numEntities = initialValues.entities.length ?? 0;
    const numTestamentaryEntities =
      initialValues.testamentaryEntities.length ?? 0;

    switch (variant) {
      case AIOnboardingModalVariant.ALL_ENTITIES:
        return {
          title: getAIOnboardingModalHeader([
            {
              count: numEntities + numTestamentaryEntities,
              nounSingular: 'entity',
              nounPlural: 'entities',
            },
          ]),
          subtitle: 'Verify the accuracy of the identified entities below',
          tableConfigs: compact([
            numEntities && {
              header: 'Active entities',
              formPath:
                'entities' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
            numTestamentaryEntities && {
              header: 'Sub-trusts',
              formPath:
                'testamentaryEntities' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
          ]),
        };
      case AIOnboardingModalVariant.TRUST_ENTITIES:
        return {
          title: getAIOnboardingModalHeader([
            {
              count: numEntities + numTestamentaryEntities,
              nounSingular: 'trust',
              nounPlural: 'trusts',
            },
          ]),
          subtitle: 'Verify the accuracy of the identified trusts below',
          tableConfigs: compact([
            numEntities && {
              header: 'Active trusts',
              formPath:
                'entities' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
            numTestamentaryEntities && {
              header: 'Sub-trusts',
              formPath:
                'testamentaryEntities' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
          ]),
        };
      case AIOnboardingModalVariant.BUSINESS_ENTITIES:
        return {
          title: getAIOnboardingModalHeader([
            {
              count: numEntities + numTestamentaryEntities,
              nounSingular: 'business',
              nounPlural: 'businesses',
            },
          ]),
          subtitle: 'Verify the accuracy of the identified businesses below',
          tableConfigs: compact([
            numEntities && {
              header: 'Businesses',
              formPath:
                'entities' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
            numTestamentaryEntities && {
              header: 'Sub-trusts',
              formPath:
                'testamentaryEntities' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
          ]),
        };
      case AIOnboardingModalVariant.HOLDINGS:
        const numAssets = initialValues.assets.length ?? 0;
        const numLiabilities = initialValues.liabilities.length ?? 0;
        const numLifeInsurancePolicies =
          initialValues.lifeInsurancePolicies.length ?? 0;
        return {
          title: getAIOnboardingModalHeader([
            {
              count: numAssets,
              nounSingular: 'asset',
              nounPlural: 'assets',
            },
            {
              count: numLiabilities,
              nounSingular: 'liability',
              nounPlural: 'liabilities',
            },
            {
              count: numLifeInsurancePolicies,
              nounSingular: 'life insurance policy',
              nounPlural: 'life insurance policies',
            },
          ]),
          subtitle:
            'Luminary AI can make mistakes. Please double check for accuracy',
          tableConfigs: compact([
            numAssets && {
              header: 'Assets',
              formPath:
                'assets' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
            numLiabilities && {
              header: 'Liabilities',
              formPath:
                'liabilities' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
            numLifeInsurancePolicies && {
              header: 'Life insurance policies',
              formPath:
                'lifeInsurancePolicies' as const satisfies PathsOf<AIOnboardingModalFormShape>,
            },
          ]),
        };
      default:
        throw new UnreachableError({
          case: variant,
          message: 'unknown variant in AIOnboardingModalRightContent',
        });
    }
  }, [
    initialValues.assets.length,
    initialValues.entities.length,
    initialValues.liabilities.length,
    initialValues.lifeInsurancePolicies.length,
    initialValues.testamentaryEntities.length,
    variant,
  ]);

  return (
    <FormProvider {...formMethods}>
      <FormFieldsDisabledProvider isDisabled={isOperationInFlight}>
        {title && (
          <Stack gap={1}>
            <Typography variant="title-l">{title}</Typography>
            <Typography variant="body-xs">{subtitle}</Typography>
          </Stack>
        )}
        <AIOnboardingModalFormInner
          initialValues={initialValues}
          formTableConfigs={tableConfigs}
        />
      </FormFieldsDisabledProvider>
    </FormProvider>
  );
}
